import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatExpansionPanel } from '@angular/material/expansion';
import { ESkeletonType } from '@norfolk-southern/accessns-components';
import { AgGridAngular } from 'ag-grid-angular';
import { Column } from 'ag-grid-community';
import { addDays } from 'date-fns';

import { NSCustomizedReportingGridConstants } from '@ruby/configs/customized-reporting-grid.constants';
import { ICRFavoriteReport } from '@ruby/modules/customized-reporting/models/report.interface';
import {
  IUnitTrainFilters,
  IUTEmptyPermit,
  IUTFiltersConfigurations,
  IUTFiltersExtraConfigurations,
  IUTGridData,
  IUTReleaseCars,
  IUTReleaseEmptiesHeader,
  IUTTrainConsist
} from '@ruby/modules/unit-train/models/unit-train.interface';
import { EGridType } from '@ruby/shared/enums/grid.enums';
import { EUTGridType } from '@ruby/shared/enums/unit-train.enums';
import { IGridConfigurations } from '@ruby/shared/models/commons/grid.interface';
import { GridCustomizedReportingService } from '@ruby/shared/services/grid-customized-reporting.service';

@Component({
  selector: 'ng-ut-results-grid',
  templateUrl: './ut-results-grid.component.html',
  styleUrls: ['./ut-results-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UTResultsGridComponent implements OnChanges {
  @Input() gridConfigs?: IGridConfigurations;
  @Input() filtersConfigs?: Partial<IUTFiltersConfigurations>;
  @Input() filtersExtraConfigs?: Partial<IUTFiltersExtraConfigurations>;
  @Input() loading!: boolean;
  @Input() data: Array<Partial<IUTGridData>> = [];
  @Input() type!: EUTGridType;
  @Input() releaseDate?: number;
  @Input() emptyPermits: Array<IUTEmptyPermit> = [];
  @Input() gridInitialized = false;
  @Input() trainConsist?: IUTTrainConsist;
  @Input() releaseEmptiesHeader?: IUTReleaseEmptiesHeader;
  @Output() search = new EventEmitter<IUnitTrainFilters>();
  @Output() next = new EventEmitter<Partial<IUTGridData>>();
  @Output() releaseCars = new EventEmitter<Partial<IUTReleaseCars>>();
  @ViewChild('expansionPanelFilters') filtersPanel?: MatExpansionPanel;
  eUTGridType = EUTGridType;
  releaseEmptiesForm = new UntypedFormGroup({
    timezone: new UntypedFormControl('L'),
    carsPlaced: new UntypedFormControl(),
    dumpingStarted: new UntypedFormControl(),
    dumpingFinished: new UntypedFormControl(),
    releaseDate: new UntypedFormControl('', [Validators.required]),
    totalWeight: new UntypedFormControl(),
    weightScale: new UntypedFormControl('T')
  });
  grid!: AgGridAngular;
  yesterday: Date = addDays(new Date(), -1);
  tomorrow: Date = addDays(new Date(), 1);
  skeleton: ESkeletonType = ESkeletonType.PIPE;
  private filters?: IUnitTrainFilters;

  constructor(private customizedReportingService: GridCustomizedReportingService) {}

  get subtitleMsg(): string {
    let subtitleMsg = '';
    if (this.type === EUTGridType.SHIPMENT_STATUS) {
      const count = this.data.filter((value: Partial<IUTGridData>) => value.permitNumber).length;
      subtitleMsg = `Total Shipments (${ count })`;
    }
    return subtitleMsg;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.releaseDate && this.releaseDate) {
      this.releaseEmptiesForm.controls.releaseDate.setValue(new Date(this.releaseDate));
    }
  }

  /**
   * Set Grid
   *
   * @summary Sets the grid instance
   * @param $grid: AgGridAngular
   * @returns void
   */
  setGrid($grid: AgGridAngular): void {
    this.grid = $grid;
  }

  /**
   * Save Filters
   *
   * @summary set filters and makes a call to search with filters
   * @param $filters: IUnitTrainFilters
   * @return void
   */
  saveFilters($filters: IUnitTrainFilters): void {
    this.filtersPanel!.expanded = false;
    this.filters = $filters;
    if (this.type === EUTGridType.TRAIN_SET_INQUIRY) {
      const nextSchColumn: Column | undefined = this.grid.columnApi.getAllGridColumns().find((c: Column) => c.getColDef().field === 'nextSchDeliveryDate');
      const nextLoadDtColumn: Column | undefined = this.grid.columnApi.getAllGridColumns().find((c: Column) => c.getColDef().field === 'nextLoadDate');
      if (nextSchColumn) {
        this.grid.columnApi.setColumnVisible(nextSchColumn.getColId(), this.filters.commodities.includes('ET'));
      }
      if (nextLoadDtColumn) {
        this.grid.columnApi.setColumnVisible(nextLoadDtColumn.getColId(), !this.filters.commodities.includes('ET'));
      }
    }
    this.emitMakeAdvanceSearch();
  }

  /**
   * Emit Make Advance Search
   *
   * @summary emit an event to make a search
   * @return void
   */
  emitMakeAdvanceSearch(): void {
    if (this.filters) {
      this.search.emit(this.filters);
    }
  }

  /**
   * Emit Next
   *
   * @summary emits a next event
   * @returns void
   */
  emitNext(): void {
    this.next.emit({ ...this.grid.api.getSelectedRows()[0], emptyPermits: this.emptyPermits });
  }

  /**
   * Confirm Release
   *
   * @summary emits a confirm release event
   * @returns void
   */
  confirmRelease(): void {
    this.releaseCars.emit({
      carsPlacedDate: this.releaseEmptiesForm.controls.carsPlaced.value?.getTime(),
      destinationWeightInTons: this.releaseEmptiesForm.controls.totalWeight.value || undefined,
      dumpedFinishedDate: this.releaseEmptiesForm.controls.dumpingFinished.value?.getTime(),
      dumpedstartDate: this.releaseEmptiesForm.controls.dumpingStarted.value?.getTime(),
      formattedReleaseddate: this.releaseEmptiesForm.controls.releaseDate.value?.getTime(),
      nonPactcars: this.grid.api.getSelectedRows().filter((r: Partial<IUTGridData>) => !r.isChecked),
      timezone: this.releaseEmptiesForm.controls.timezone.value || undefined,
      weightScale: this.releaseEmptiesForm.controls.weightScale.value || undefined,
      pactcars: [],
      permit: this.releaseEmptiesHeader?.permit,
      searchText: this.releaseEmptiesHeader?.searchText
    });
  }

  /**
   * Create Customized Report
   *
   * @summary call create customized report
   * @param $filters: { filters: Partial<IUnitTrainFilters>; formValue: string }
   * @returns void
   */
  createReport($filters: { filters: Partial<IUnitTrainFilters>; formValue: string; selectedReport?: ICRFavoriteReport }): void {
    this.customizedReportingService.createCustomizedReport(
      this.gridConfigs!.gridTitle,
      this.gridConfigs!.gridName,
      NSCustomizedReportingGridConstants.getGridData(this.gridConfigs!.gridName, this.grid),
      [],
      NSCustomizedReportingGridConstants.getGridOptions(this.gridConfigs!.gridName as EGridType, $filters.formValue, JSON.stringify($filters.filters)),
      $filters.selectedReport
    );
  }
}
